import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import ImpersonationService, {
    ImpersonationData,
} from '../services/ImpersonationService';

/**
 * Impersonation Context Interface
 */
interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonationData: ImpersonationData | null;
  initializeImpersonation: () => void;
  clearImpersonation: () => void;
  getImpersonationToken: () => string | null;
  endSession: () => void;
  remainingTime: number | null;
  refreshImpersonationData: () => void;
}

/**
 * Impersonation Provider Props
 */
interface ImpersonationProviderProps {
  children: ReactNode;
}

// Create Context
const ImpersonationContext = createContext<ImpersonationContextType | undefined>(
  undefined
);

/**
 * Impersonation Provider Component
 * Manages impersonation state across the application
 */
export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({
  children,
}) => {
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonationData, setImpersonationData] =
    useState<ImpersonationData | null>(null);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);

  /**
   * Initialize impersonation from URL params or sessionStorage
   */
  const initializeImpersonation = useCallback(() => {
    // Check URL params for impersonation token (for new tab)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('impersonation_token');
    const sessionFromUrl = urlParams.get('session');

    if (tokenFromUrl && sessionFromUrl) {
      // Decode and parse the token to get expiry info
      try {
        const tokenPayload = JSON.parse(atob(tokenFromUrl.split('.')[1]));
        const expiresAt = new Date(tokenPayload.exp * 1000).toISOString();

        const data: ImpersonationData = {
          token: tokenFromUrl,
          tokenType: 'bearer',
          expiresIn: tokenPayload.exp - Math.floor(Date.now() / 1000),
          expiresAt: expiresAt,
          sessionUuid: sessionFromUrl,
          candidate: {
            uuid: tokenPayload.user?.uuid || '',
            name: `${tokenPayload.user?.first_name || ''} ${tokenPayload.user?.last_name || ''}`.trim(),
            email: tokenPayload.user?.email || '',
          },
          isImpersonation: tokenPayload.impersonation?.is_impersonated || true,
        };

        ImpersonationService.storeImpersonationData(data);
        setImpersonationData(data);
        setIsImpersonating(true);

        // Clean URL params without reloading
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        return;
      } catch (error) {
        console.error('Failed to parse impersonation token:', error);
      }
    }

    // Check sessionStorage for existing impersonation
    const storedData = ImpersonationService.getStoredImpersonationData();
    if (storedData && ImpersonationService.isImpersonating()) {
      setImpersonationData(storedData);
      setIsImpersonating(true);
    }
  }, []);

  /**
   * Clear impersonation state
   */
  const clearImpersonation = useCallback(() => {
    ImpersonationService.clearImpersonationData();
    setImpersonationData(null);
    setIsImpersonating(false);
    setRemainingTime(null);
  }, []);

  /**
   * End the current impersonation session
   * Just clears local data and closes the tab - no API call needed
   * The session will expire on its own, or admin can revoke from their panel
   */
  const endSession = useCallback(() => {
    clearImpersonation();
    // Close the tab - this should work since it was opened by window.open()
    window.close();
    // If window.close() doesn't work, show a message instead of redirecting
    // (redirecting could cause issues with admin session)
    setTimeout(() => {
      if (!window.closed) {
        alert('Please close this tab manually to return to the admin panel.');
      }
    }, 200);
  }, [clearImpersonation]);

  /**
   * Get the current impersonation token
   */
  const getImpersonationToken = useCallback((): string | null => {
    return ImpersonationService.getImpersonationToken();
  }, []);

  /**
   * Refresh impersonation data from sessionStorage
   * Call this after updating sessionStorage externally (e.g., from ImpersonationEntry)
   */
  const refreshImpersonationData = useCallback(() => {
    const storedData = ImpersonationService.getStoredImpersonationData();
    if (storedData && ImpersonationService.isImpersonating()) {
      setImpersonationData(storedData);
      setIsImpersonating(true);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeImpersonation();
  }, [initializeImpersonation]);

  // Update remaining time every second
  useEffect(() => {
    if (!isImpersonating || !impersonationData?.expiresAt) {
      return;
    }

    const updateRemainingTime = () => {
      const expiresAt = new Date(impersonationData.expiresAt);
      const now = new Date();
      const diff = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));

      if (diff === 0) {
        clearImpersonation();
        // Show session expired message
        alert('Your impersonation session has expired.');
        window.close();
      } else {
        setRemainingTime(diff);
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [isImpersonating, impersonationData, clearImpersonation]);

  const value: ImpersonationContextType = useMemo(
    () => ({
      isImpersonating,
      impersonationData,
      initializeImpersonation,
      clearImpersonation,
      getImpersonationToken,
      endSession,
      remainingTime,
      refreshImpersonationData,
    }),
    [
      isImpersonating,
      impersonationData,
      initializeImpersonation,
      clearImpersonation,
      getImpersonationToken,
      endSession,
      remainingTime,
      refreshImpersonationData,
    ]
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

/**
 * Custom hook to use the Impersonation Context
 */
export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export default ImpersonationContext;
